import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonLayout from '../layouts/CommonLayout';
import { uploadImage, deleteImage } from '../../api/ImageApi';
import { changeProfileImage, active, me } from '../../api/MemberApi';
import LocationSelectionContainer from '../containers/LocationSelectionContainer';
import useAuthStore from '../../store/authStore';
import { AlertModal } from "../layouts/AlertModal"; // AlertModal 컴포넌트를 import 합니다.

const WelcomePage = () => {
    const navigate = useNavigate();
    const { setMember } = useAuthStore();
    const [uploadedImageId, setUploadedImageId] = useState(null);
    const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
    const [currentStep, setCurrentStep] = useState('profile'); // 'profile' 또는 'location'

    // AlertModal 상태 관리 (콜백 함수 포함)
    const [alertState, setAlertState] = useState({
        isVisible: false,
        message: '',
        callback: null,
    });

    // API 에러 메시지를 안전하게 추출하는 헬퍼 함수
    const getErrorMessage = (error) => {
        if (error.response && error.response.data && error.response.data.message) {
            return error.response.data.message;
        }
        return '오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const response = await uploadImage(file);
            setUploadedImageId(response.id);
            setUploadedImageUrl(response.url);
        } catch (error) {
            setAlertState({ isVisible: true, message: getErrorMessage(error), callback: null });
        }
    };

    const handleImageDelete = async () => {
        if (!uploadedImageId) return;

        try {
            await deleteImage(uploadedImageId);
            setUploadedImageId(null);
            setUploadedImageUrl(null);
        } catch (error) {
            console.error('이미지 삭제 실패:', error);
            setAlertState({ isVisible: true, message: getErrorMessage(error), callback: null });
        }
    };

    const handleSaveProfile = async () => {
        if (!uploadedImageId) {
            setAlertState({ isVisible: true, message: '프로필 사진을 업로드해주세요.', callback: null });
            return;
        }

        try {
            await changeProfileImage(uploadedImageId);
            setCurrentStep('location');
        } catch (error) {
            setAlertState({ isVisible: true, message: getErrorMessage(error), callback: null });
        }
    };

    const handleStartWithoutPhoto = async () => {
        setCurrentStep('location');
    };

    const handleLocationSaved = async () => {
        try {
            // 위치 저장 후 계정 활성화
            await active();

            // 멤버 정보 업데이트
            const updatedMember = await me();
            setMember(updatedMember);

            navigate('/app/meetings', { replace: true });
        } catch (error) {
            console.error('계정 활성화 또는 멤버 정보 업데이트 실패:', error);
            setAlertState({ isVisible: true, message: '계정 활성화에 실패했습니다.', callback: null });
        }
    };

    // 프로필 설정 단계 렌더링
    const renderProfileStep = () => (
        <div className="flex flex-col h-[calc(100vh-64px)] p-4">
            {/* 상단: 축하 문구 */}
            <div className="flex flex-col flex-1 items-center justify-center">
                <h1 className="text-3xl font-semibold text-gray-800">
                    밥친구 가입을 축하합니다!
                </h1>
                <p className="text-md text-gray-500 mt-4 text-center">
                    프로필 사진을 업로드하여 나를 멋지게 표현해보세요.
                </p>
            </div>

            {/* 중단: 프로필 사진 업로드 */}
            <div className="flex-1 flex flex-col items-center justify-center space-y-6">
                <div className="w-32 h-32 rounded-full bg-gray-300 overflow-hidden flex items-center justify-center">
                    {uploadedImageUrl ? (
                        <img src={uploadedImageUrl} alt="프로필" className="w-full h-full object-cover" />
                    ) : (
                        <span className="text-gray-500 text-sm">사진</span>
                    )}
                </div>

                <div className="flex space-x-4">
                    <label className="px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer">
                        업로드
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                        />
                    </label>
                    <button
                        onClick={handleImageDelete}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg"
                        disabled={!uploadedImageUrl}
                    >
                        삭제
                    </button>
                </div>
            </div>

            {/* 하단: 버튼 */}
            <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-sm space-y-3">
                    <button
                        onClick={handleSaveProfile}
                        className="w-full py-3 bg-amber-500 text-white rounded-lg"
                        disabled={!uploadedImageId}
                    >
                        프로필 사진 저장
                    </button>
                    <button
                        onClick={handleStartWithoutPhoto}
                        className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg"
                    >
                        프로필 사진 없이 시작하기
                    </button>
                </div>
            </div>
        </div>
    );

    // 위치 설정 단계 렌더링
    const renderLocationStep = () => (
        <div className="h-[calc(100vh-65px)]">
            <LocationSelectionContainer onLocationSaved={handleLocationSaved} />
        </div>
    );

    return (
        <CommonLayout
            title={currentStep === 'profile' ? "환영합니다" : "위치 설정"}
            showBackButton={false}
        >
            <AlertModal
                isVisible={alertState.isVisible}
                title={alertState.title}
                message={alertState.message}
                callback={alertState.callback}
                onClose={() => setAlertState({ isVisible: false, title: '', message: '', callback: null })}
            />
            {currentStep === 'profile' ? renderProfileStep() : renderLocationStep()}
        </CommonLayout>
    );
};

export default WelcomePage;
